import {useEffect, useMemo, useRef, useState} from 'react'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import OrganizationService from '../core/_requests'
import {
  applyTrendResponse,
  buildOrganizationAnalyticsPayload,
  clearTrendBreakoutData,
  EMPTY_SHIPMENT_TREND_DATA,
  getActiveTrendSeries,
  hasTrendChartData,
  OrganizationAnalyticsFilters,
  ShipmentTrendBreakout,
  ShipmentTrendDataByBreakout,
} from '../helpers/organizationAnalyticsHelpers'

export const useShipmentTrendData = (
  activeView: ShipmentTrendBreakout,
  analyticsFilters: OrganizationAnalyticsFilters
) => {
  const {newCancelToken, isCancel} = useCancelToken()
  const activeViewRef = useRef(activeView)
  const [trendData, setTrendData] = useState<ShipmentTrendDataByBreakout>(EMPTY_SHIPMENT_TREND_DATA)
  const [isLoading, setIsLoading] = useState(true)

  activeViewRef.current = activeView

  const {accountIds, timeRange, isCustomRange, dateRange} = analyticsFilters

  useEffect(() => {
    const requestView = activeView
    let isEffectActive = true

    setIsLoading(true)

    setTrendData((prev) => {
      if (!isEffectActive) {
        return prev
      }

      const patch = clearTrendBreakoutData(requestView)
      const key = requestView === 'overall' ? 'overall' : requestView
      const current = prev[key]

      if (Array.isArray(current) && current.length === 0) {
        return prev
      }

      return {...prev, ...patch}
    })

    if (!accountIds.length) {
      if (isEffectActive) {
        setIsLoading(false)
      }
      return () => {
        isEffectActive = false
      }
    }

    const loadShipmentTrendData = async () => {
      try {
        const response = await OrganizationService.getShipmentsTrendData(
          buildOrganizationAnalyticsPayload(
            {accountIds, timeRange, isCustomRange, dateRange},
            requestView
          ),
          {cancelToken: newCancelToken()}
        )

        if (!isEffectActive || requestView !== activeViewRef.current || !response?.data) {
          return
        }

        const nextData = applyTrendResponse(requestView, response.data)
        if (nextData) {
          setTrendData((prev) => ({...prev, ...nextData}))
        }
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        if (isEffectActive && requestView === activeViewRef.current) {
          setIsLoading(false)
        }
      }
    }

    loadShipmentTrendData()
    return () => {
      isEffectActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountIds,
    activeView,
    newCancelToken,
    dateRange.start,
    dateRange.end,
    isCancel,
    isCustomRange,
    timeRange,
  ])

  const seriesTrendData = useMemo(
    () => getActiveTrendSeries(activeView, trendData),
    [activeView, trendData]
  )

  return {
    trendData,
    seriesTrendData,
    hasChartData: hasTrendChartData(activeView, trendData),
    isLoading,
  }
}
